import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  subscribeAppSyncEvents,
  publishAppSyncEvent,
  appsyncEventEndpoints,
  type AppSyncEventsUnsubscribe,
} from '../services/appsyncEvents';

export type WorkbenchEvent = Record<string, unknown>;

/**
 * useWorkbenchChannel — collaborative IDE subscription for
 *   ``/default/workbench/{tenantId}/{networkId}``
 *
 * This channel is distinct from the per-pool agent channel
 * (``useAgentChannel``) and fans out editing-time events: presence
 * beacons, field locks, and ``FIELD_UPDATED`` invalidation signals. The
 * AppSync authorizer gates subscribers to BUILD_ROLES
 * (OWNER / ADMIN / CREATOR) of the matching tenant.
 *
 * Design notes:
 * - **Shared WebSocket.** Transport is the ``services/appsyncEvents``
 *   singleton — this hook adds a multiplexed subscription on the existing
 *   connection, not a new one.
 * - **Presence heartbeat** is driven by the hook and is *presence-aware*:
 *   while the tab is visible AND at least one other editor is present, we
 *   republish a ``WORKBENCH_PRESENCE_JOINED`` beacon every 30s so the
 *   roster naturally ages out crashed tabs (clients keep a local ~45s TTL
 *   on each sub they see). **Alone in the room we go silent** — one JOINED
 *   "knock" on subscribe, then zero ``POST /event`` traffic until a peer
 *   arrives. A newcomer's JOINED triggers an immediate echo from us (so
 *   they see us at once) and re-arms the heartbeat for both sides.
 * - **Visibility-aware**: while ``document.visibilityState === 'hidden'``
 *   the heartbeat publish is skipped (the local sweep still runs so our
 *   own roster is correct). On visibility return we immediately publish
 *   ``JOINED`` so peers see us back without waiting up to 30s. Net
 *   effect: an idle background tab generates **zero** ``POST /event``
 *   traffic.
 * - **HTTP ``POST …/event``** is how the browser *publishes* those
 *   beacons (AppSync Events is WS-in, HTTP-out). DevTools often shows
 *   **two** lines per publish (CORS **OPTIONS** + **POST**), which is
 *   not the same as a 30s poll loop.
 * - **Presence snapshot** is derived client-side from live events — we
 *   do not hit DynamoDB for it. This keeps the channel Slack-like in
 *   cost.
 * - **No auto-persist** by the client: the backend owns persistence for
 *   the handful of events that need historical replay
 *   (``FIELD_UPDATED``); the frontend never writes to the events table.
 */

const PRESENCE_HEARTBEAT_MS = 30_000;
const PRESENCE_TTL_MS = 45_000;

export type PresenceEntry = {
  sub: string;
  email?: string;
  lastSeenMs: number;
  typing?: boolean;
  typingField?: string;
};

export type UseWorkbenchChannelOptions = {
  /**
   * When false, the hook does not open the AppSync subscription or send
   * presence HTTP beacons. Use on tabs that only need the agent build
   * stream (e.g. Build) to avoid a third realtime subscription + repeated
   * ``POST …/event`` traffic.
   */
  enabled?: boolean;
};

export function useWorkbenchChannel(
  networkId: string | undefined,
  tenantId: string | undefined,
  currentUserSub: string | undefined,
  options?: UseWorkbenchChannelOptions,
) {
  const enabled = options?.enabled !== false;
  const [events, setEvents] = useState<WorkbenchEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [presence, setPresence] = useState<Record<string, PresenceEntry>>({});

  const { httpDns, realtimeDns } = appsyncEventEndpoints();

  const channelPath = useMemo(
    () =>
      tenantId && networkId
        ? `/default/workbench/${tenantId}/${networkId}`
        : undefined,
    [tenantId, networkId],
  );
  const publishChannel = useMemo(
    () =>
      tenantId && networkId
        ? `default/workbench/${tenantId}/${networkId}`
        : undefined,
    [tenantId, networkId],
  );

  // Shared publisher — used by the heartbeat, typing beacons, and the
  // public ``publish`` escape hatch. Does NOT hit the REST API; presence
  // events are AppSync-only and deliberately never persisted.
  const publish = useCallback(
    async (payload: Record<string, unknown>) => {
      if (!enabled) return;
      if (!publishChannel || !httpDns) {
        throw new Error('Workbench channel: missing tenant/network or HTTP DNS');
      }
      await publishAppSyncEvent(httpDns, publishChannel, payload);
    },
    [enabled, publishChannel, httpDns],
  );

  // Convenience wrappers around ``publish`` for the two high-frequency
  // beacons the UI needs; components that want to raise other event types
  // can call ``publish`` directly.
  const sendTyping = useCallback(
    (field?: string) => {
      if (!currentUserSub) return Promise.resolve();
      return publish({
        eventType: 'WORKBENCH_TYPING',
        actorSub: currentUserSub,
        payload: field
          ? { sub: currentUserSub, field }
          : { sub: currentUserSub },
        timestamp: new Date().toISOString(),
      }).catch(() => undefined);
    },
    [publish, currentUserSub],
  );

  const sendPresenceBeacon = useCallback(
    (kind: 'JOINED' | 'LEFT') => {
      if (!currentUserSub) return Promise.resolve();
      return publish({
        eventType:
          kind === 'JOINED'
            ? 'WORKBENCH_PRESENCE_JOINED'
            : 'WORKBENCH_PRESENCE_LEFT',
        actorSub: currentUserSub,
        payload: { sub: currentUserSub },
        timestamp: new Date().toISOString(),
      }).catch(() => undefined);
    },
    [publish, currentUserSub],
  );

  // Keep latest beacon impl for the subscribe effect without listing
  // ``sendPresenceBeacon`` in that effect's deps — a new callback
  // identity would otherwise tear the subscription down and spam
  // ``POST …/event`` (JOINED on every subscribe_success + LEFT on every
  // cleanup) even when channel keys are unchanged.
  const sendPresenceBeaconRef = useRef(sendPresenceBeacon);
  sendPresenceBeaconRef.current = sendPresenceBeacon;

  // Read the current user's sub through a ref inside the subscribe effect so
  // a sub change doesn't re-list itself in the effect deps (which would tear
  // the subscription down and re-spam JOINED/LEFT). Used only to exclude self
  // from the ``knownPeers`` set that gates the heartbeat.
  const currentUserSubRef = useRef(currentUserSub);
  currentUserSubRef.current = currentUserSub;

  // Resubscribe triggers: network/tenant change resets the local buffer.
  useEffect(() => {
    setEvents([]);
    setPresence({});
  }, [networkId, tenantId]);

  useEffect(() => {
    if (!enabled || !channelPath || !httpDns || !realtimeDns) {
      setConnected(false);
      return undefined;
    }

    setError(null);
    let active = true;
    // One JOINED publish per physical subscribe (guards duplicate
    // ``subscribe_success`` if the shared socket reconnects under us).
    let presenceJoinSentForThisSub = false;
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    // Set by ``applyPresenceEvent`` when a previously-unseen peer appears in
    // the current ``onData`` batch; ``onData`` reads it to echo JOINED once.
    let sawNewPeerThisBatch = false;

    // Presence-aware heartbeat: we only re-publish the 30s JOINED beacon
    // while at least one *other* editor is present. Solo in the room → no
    // ongoing ``POST /event`` traffic at all. ``knownPeers`` is the set of
    // other subs we've heard from (self excluded); a newcomer growing this
    // set triggers an immediate JOINED echo (see ``onData``) so they see us
    // right away rather than waiting up to 30s for the next tick.
    const knownPeers = new Set<string>();

    const applyPresenceEvent = (evt: WorkbenchEvent): void => {
      const type = String(evt.eventType || '').toUpperCase();
      const payload = (evt.payload || {}) as Record<string, unknown>;
      const sub = String(
        (evt.actorSub as string) || payload.sub || '',
      ).trim();
      if (!sub) return;
      // Maintain the peer set (drives heartbeat gating + newcomer echo).
      // Self is never a "peer" — own events echoed back must not keep us
      // beaconing forever in an otherwise-empty room.
      if (sub !== currentUserSubRef.current) {
        if (type === 'WORKBENCH_PRESENCE_LEFT') {
          knownPeers.delete(sub);
        } else if (!knownPeers.has(sub)) {
          knownPeers.add(sub);
          sawNewPeerThisBatch = true;
        }
      }
      const email = (payload.email as string | undefined) || undefined;
      const now = Date.now();
      setPresence((prev) => {
        const next = { ...prev };
        if (type === 'WORKBENCH_PRESENCE_LEFT') {
          delete next[sub];
          return next;
        }
        const existing = next[sub];
        next[sub] = {
          sub,
          email: email || existing?.email,
          lastSeenMs: now,
          typing: type === 'WORKBENCH_TYPING',
          typingField:
            type === 'WORKBENCH_TYPING'
              ? (payload.field as string | undefined)
              : existing?.typingField,
        };
        return next;
      });
    };

    const unsubscribe: AppSyncEventsUnsubscribe = subscribeAppSyncEvents(
      httpDns,
      realtimeDns,
      channelPath,
      {
        onData: (list) => {
          if (!active) return;
          const parsed: WorkbenchEvent[] = list.map((e) => {
            if (typeof e === 'string') {
              try {
                return JSON.parse(e) as WorkbenchEvent;
              } catch {
                return { raw: e } as WorkbenchEvent;
              }
            }
            if (e && typeof e === 'object' && !Array.isArray(e)) {
              return e as WorkbenchEvent;
            }
            return { raw: e } as WorkbenchEvent;
          });
          // Presence is purely derived state — the UI rarely needs the
          // raw WORKBENCH_TYPING row, but keeping it in ``events`` lets
          // consumers debug with "tail -f" semantics.
          sawNewPeerThisBatch = false;
          parsed.forEach(applyPresenceEvent);
          // A newcomer just announced themselves — echo JOINED once so they
          // see us immediately (and so the heartbeat below now has a peer to
          // beat for). Skipped while hidden, mirroring the heartbeat policy.
          if (sawNewPeerThisBatch && !isHidden()) {
            void sendPresenceBeaconRef.current('JOINED');
          }
          setEvents((prev) => [...prev, ...parsed]);
        },
        onSubscribed: () => {
          if (!active) return;
          setConnected(true);
          // Announce ourselves once the subscribe handshake completes so
          // other editors see us instantly (otherwise they'd wait up to
          // 30s for the first heartbeat tick). Re-arm on reconnect by
          // clearing the latch when the socket drops (see onStatus).
          if (!presenceJoinSentForThisSub) {
            presenceJoinSentForThisSub = true;
            void sendPresenceBeaconRef.current('JOINED');
          }
        },
        onStatus: (status) => {
          if (!active) return;
          if (status === 'connected') {
            setConnected(true);
          } else {
            setConnected(false);
            if (status === 'disconnected') {
              // Let the next ``subscribe_success`` re-broadcast JOINED
              // so peers who joined while we were offline see us again.
              presenceJoinSentForThisSub = false;
            }
          }
        },
        onError: (err) => {
          if (!active) return;
          setError(
            typeof err === 'string' ? err : JSON.stringify(err ?? {}),
          );
        },
      },
    );

    // Heartbeat — keeps this client visible to every other editor and
    // sweeps stale entries out of the local presence map; DynamoDB
    // doesn't back this view so the client is the source of truth on
    // expiry.
    //
    // While the tab is hidden we still run the local sweep (so stale
    // peers age out of *our* roster on time) but skip the outbound
    // ``POST /event`` so background tabs don't generate publish traffic.
    // When the tab becomes visible again we immediately publish JOINED
    // so peers see us back without waiting for the next interval tick.
    const isHidden = () =>
      typeof document !== 'undefined' && document.visibilityState === 'hidden';

    heartbeatTimer = setInterval(() => {
      // Only beacon while the tab is visible AND someone else is here to
      // care. Alone in the room → the local sweep below still runs, but we
      // emit zero ``POST /event`` traffic until a peer arrives.
      if (!isHidden() && knownPeers.size > 0) {
        void sendPresenceBeaconRef.current('JOINED');
      }
      setPresence((prev) => {
        const now = Date.now();
        const next: Record<string, PresenceEntry> = {};
        let dirty = false;
        for (const [k, v] of Object.entries(prev)) {
          if (now - v.lastSeenMs <= PRESENCE_TTL_MS) {
            next[k] = v;
          } else {
            dirty = true;
          }
        }
        return dirty ? next : prev;
      });
    }, PRESENCE_HEARTBEAT_MS);

    const onVisibilityChange = () => {
      if (!active) return;
      // Re-announce on return only if there's a peer to announce to.
      if (!isHidden() && knownPeers.size > 0) {
        void sendPresenceBeaconRef.current('JOINED');
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      active = false;
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      // Best-effort LEFT beacon — if the token is still fresh the server
      // will fan it out to peers before we drop the subscription.
      void sendPresenceBeaconRef.current('LEFT');
      unsubscribe();
    };
  }, [enabled, channelPath, httpDns, realtimeDns]);

  return {
    events,
    presence,
    connected,
    error,
    publish,
    sendTyping,
  };
}
